/**
 * 格式化消息列表中的消息，用于前端渲染
 */

import uuid from 'uuid/v4'
import dayjs from 'dayjs'
import {
    HOST,
    STATUS,
    EVALUATION_VALUE,
    SYSTEM_MESSAGE,
    MESSAGE_NAME,
    MESSAGE_FROM
} from '@/constants/message'
import {
    nowTimestamp
} from '@/utils'
import humanUsers from '@/utils/human_user_info'
import formatMessageTime from './time'
import formatMessageEventNormal from './event_normal'
import formatMessageEventSocket from './event_socket'
import formatMessageEventQueue from './event_queue'
import formatMessageEventChatNotice from './event_chat_notice'
import formatMessageEventWithdraw from './event_withdraw'
import formatMessageContentText from './text'
import formatMessageContentImage from './image'
import formatMessageContentFile from './file'
import formatMessageContentAudio from './audio'
import formatMessageContentAudioTranslation from './audio_translation'
import formatMessageContentVideo from './video'
import formatMessageContentRichText from './rich_text'
import formatMessageContentHtml from './html'
import formatMessageContentRenderText from './render_text'
import formatMessageContentSelect from './select'
import formatMessageContentTable from './table'
import formatMessageContentOptionNormal from './option_normal'
// import formatMessageContentOptionSwiper from './option_swiper'
import formatMessageSwiperList from './swiper_list'
import formatMessageContentSwiperImgText from './swiper_img_text'
// import formatMessageContentSingle from './swiper_single'
import formatMessageContentSwiperIcon from './swiper_icon'

// http://192.168.3.101/weixu/doc/blob/master/webot_new/%E6%B6%88%E6%81%AF%E7%B1%BB%E5%9E%8B:webot3_msg_type.md
/**
 * 消息基类字段
 * id 消息 id
 * from_id 发送者 id
 * to_id 接收者 id
 * timestamp 创建时间戳
 * data 消息体
 */

/**
 * 会话消息类
 * from 发送方
 * to 接收方
 * source 来源
 * ext 扩展字段
 */

/**
 * 消息案例
 */
/* eslint-disable-next-line */
const exampleMessage = {
    /**
     * @param { String } name - 前端用于判断渲染哪个消息类型
     */
    name: '',
    /**
     * @param { String } id - 前端生成的 id
     * @param { Date } timestamp - 创建时间戳（前端发送的消息由前端生成，后端返回的采用后端的？存在时间差，如何解决）
     * @param { String } time - 格式化后的时间戳
     * @param { Number } status - 消息状态，客服的信息默认为 success
     * @param { String } host - 消息所属主体，机器人和人工都属于 HOST.SERVICE，用户属于 HOST.USER
     * @param { Boolean } isUnread - user 默认已读 false，服务端默认未读 true
     */
    baseProps: {
        id: uuid(),
        timestamp: nowTimestamp(),
        time: dayjs().format('YYYY-MM-DD'),
        status: STATUS.SUCCESS,
        host: HOST.SERVICE,
        isUnread: false
    },
    /**
     * 根据消息类型，content 不同，辅以多个消息类型处理函数，以文本为例
     */
    content: {
        text: ''
    },
    /**
     * 用于前端展示相关，如是否显示头像，圆角
     */
    layout: {
        isShowHost: true,
        isGroupStart: true,
        isGroupEnd: true
    },
    /**
     * robot 专属属性，如评分、是否已点击“以上都不是”。[可选]
     */
    robot: {
        evaluationValue: EVALUATION_VALUE.NONE, // 后端的 is_solved 字段
        hasClickAllNot: false // 后端的 is_all_not 字段
    },
    /**
     * 前端用到的服务器返回的数据[可选]
     */
    server: {
        id: 'xx',
        timestamp: new Date().valueOf(),
        from: '',
        from_id: ''
    }
}

/**
 * 各个消息类型的 content 字段格式化
 */
const formatContentStrategies = {
    [MESSAGE_NAME.TIME]: formatMessageTime,
    [MESSAGE_NAME.EVENT_NORMAL]: formatMessageEventNormal,
    [MESSAGE_NAME.EVENT_SOCKET]: formatMessageEventSocket,
    [MESSAGE_NAME.EVENT_CHAT_NOTICE]: formatMessageEventChatNotice,
    [MESSAGE_NAME.EVENT_QUEUE]: formatMessageEventQueue,
    [MESSAGE_NAME.EVENT_WITHDRAW]: formatMessageEventWithdraw,
    [MESSAGE_NAME.TEXT]: formatMessageContentText,
    [MESSAGE_NAME.IMAGE]: formatMessageContentImage,
    [MESSAGE_NAME.FILE]: formatMessageContentFile,
    [MESSAGE_NAME.AUDIO]: formatMessageContentAudio,
    [MESSAGE_NAME.AUDIO_TRANSLATION]: formatMessageContentAudioTranslation,
    [MESSAGE_NAME.VIDEO]: formatMessageContentVideo,
    [MESSAGE_NAME.RICH_TEXT]: formatMessageContentRichText,
    [MESSAGE_NAME.HTML]: formatMessageContentHtml,
    [MESSAGE_NAME.RENDER_TEXT]: formatMessageContentRenderText,
    [MESSAGE_NAME.SELECT]: formatMessageContentSelect,
    [MESSAGE_NAME.TABLE]: formatMessageContentTable,
    [MESSAGE_NAME.OPTION_NORMAL]: formatMessageContentOptionNormal,
    // [MESSAGE_NAME.OPTION_SWIPER]: formatMessageContentOptionSwiper,
    [MESSAGE_NAME.SWIPER_LIST]: formatMessageSwiperList,
    [MESSAGE_NAME.SWIPER_IMG_TEXT]: formatMessageContentSwiperImgText,
    // [MESSAGE_NAME.SWIPER_SINGLE]: formatMessageContentSingle,
    [MESSAGE_NAME.SWIPER_ICON]: formatMessageContentSwiperIcon
}

/**
 * 格式化消息
 * @param { Object } data
 * @param { String } data.name - 消息类型（前端）
 * @param { String } data.host - 消息主体
 * @param { Number } [data.timestamp = nowTimestamp()] - 消息时间
 * @param { Number } [data.status] - 消息状态，service 端为 STATUS.SUCCESS，客户端：实时发送默认为 STATUS.SENDING，历史消息为 STATUS.SUCCESS
 * @param { Object } data.content - 消息内容，消息类型不同而不同
 * @param { Object } [data.server] - 后端返回的内容
 * @param { Boolean } [data.isUnread] - 消息是否已读，用户信息认为已读，websocket 新推送的消息认为未读，历史消息根据字段判断
 * @returns { Object }
 */
export default function formatMessage ({
    name,
    host,
    timestamp,
    status,
    content,
    server,
    isUnread,
    robot
}) {
    const baseProps = SYSTEM_MESSAGE.includes(name)
        ? formatMessageBasePropsForSystemMessage()
        : formatMessageBaseProps({
            host,
            status,
            timestamp,
            isUnread: Boolean(isUnread)
        })

    const result = {
        name,
        baseProps,
        content: formatContentStrategies[name] ? formatContentStrategies[name](content, host) : {},
        layout: getMessageLayout()
    }
    
    if (server) {
        result.server = server
        if (server.from === MESSAGE_FROM.USER && server.fromId) {
            humanUsers.add(server.fromId)
        }
    }

    if (robot) {
        result.robot = robot
    }

    return result
}

function formatMessageBaseProps ({
    host,
    status,
    timestamp,
    isUnread
}) {
    if (host === HOST.SERVICE) {
        status = STATUS.SUCCESS
    } else {
        status = typeof status === 'number' ? status : STATUS.SENDING
    }

    if (!timestamp) {
        timestamp = nowTimestamp()
    }

    const time = dayjs(timestamp).format('HH:mm')

    return {
        id: uuid(),
        timestamp,
        time,
        status,
        host,
        isUnread
    }
}

function formatMessageBasePropsForSystemMessage () {
    return {
        id: uuid()
    }
}

function getMessageLayout () {
    return {
        isShowHost: false
    }
}
